/**
 * Term frequency over time: board 05, section 5.3.
 *
 * Drawn as inline SVG rather than with a charting library. One chart does not
 * justify a dependency, and this way it inherits the theme tokens, works with
 * no network, and reverses correctly in the dark theme without a second
 * palette.
 *
 * Three decisions carry the whole chart.
 *
 * **Rate, not count, is the default.** The paper ran ninety-odd pages a day in
 * one decade and twenty in another, so raw counts describe the newspaper's
 * size rather than its attention. The count is still offered, and is still
 * what the tooltip prints, because a rate with four surviving editions behind
 * it is not the same evidence as a rate with three hundred.
 *
 * **The scanned era is shaded and the line through it is a floor.** Before the
 * digital editions begin, the series is built from recognised text, and a term
 * the recognition mangled is a term this chart cannot see. Shading the era and
 * printing the caveat with every chart is the only honest way to draw it.
 *
 * **Missing editions are drawn, not skipped.** A year the archive does not
 * hold gets a magenta band rather than a gap in the line, because a line that
 * simply continues across a hole asserts a measurement nobody made.
 *
 * Colour follows the entity, never its rank: term one keeps the accent whether
 * or not term two is removed. The series are direct-labelled at their last
 * point as well as legended, so identity is never colour alone.
 */

import { useMemo, useState } from 'react'
import type { Timeline as TimelineData, TimelinePoint } from '../lib/api'

/**
 * The fixed series order. Four hues, assigned in order and never cycled: the
 * accent, then the three content-type hues that clear CVD separation against
 * it. A fifth term is refused rather than given a generated colour.
 */
export const SERIES_COLOURS = [
  'var(--jl-accent)',
  'var(--jl-art)',
  'var(--jl-adv)',
  'var(--jl-pho)',
] as const

export const MAX_TERMS = SERIES_COLOURS.length

export interface TimelineSeries {
  term: string
  points: TimelinePoint[]
}

export type TimelineMeasure = 'rate' | 'count'

/**
 * The rate a point is plotted at: matches per 10,000 units, which is what the
 * board's caption names. `rate` is used when the server computed it; otherwise
 * it is derived from the two counts the server did give.
 */
export function rateOf(point: TimelinePoint): number {
  if (typeof point.rate === 'number') return point.rate
  if (!point.total) return 0
  return (point.matches / point.total) * 10000
}

export function valueOf(point: TimelinePoint, measure: TimelineMeasure): number {
  return measure === 'rate' ? rateOf(point) : point.matches
}

/** Every year between the first and last, so a hole in the data reads as a hole. */
function yearsOf(series: TimelineSeries[]): number[] {
  const seen = new Set<number>()
  for (const line of series) {
    for (const point of line.points) {
      const year = Number(point.period.slice(0, 4))
      if (Number.isFinite(year)) seen.add(year)
    }
  }
  if (seen.size === 0) return []
  const sorted = [...seen].sort((a, b) => a - b)
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  if (last - first > 400) return sorted
  const out: number[] = []
  for (let year = first; year <= last; year += 1) out.push(year)
  return out
}

/**
 * The contiguous runs of years the server marked as a floor, so the shading
 * follows the evidence rather than one guessed boundary. A title that ran a
 * scanned year in the middle of a digital decade gets its own band.
 */
function floorRuns(series: TimelineSeries[], years: number[]): [number, number][] {
  const flagged = new Set<number>()
  for (const line of series) {
    for (const point of line.points) {
      if (point.floor) {
        const year = Number(point.period.slice(0, 4))
        if (Number.isFinite(year)) flagged.add(year)
      }
    }
  }
  const runs: [number, number][] = []
  let start: number | null = null
  for (const year of years) {
    if (flagged.has(year)) {
      if (start === null) start = year
    } else if (start !== null) {
      runs.push([start, year - 1])
      start = null
    }
  }
  if (start !== null) runs.push([start, years[years.length - 1]])
  return runs
}

/** Contiguous runs in a sorted year list, so an absent decade is one band. */
function runsOf(years: number[]): [number, number][] {
  const sorted = [...years].sort((a, b) => a - b)
  const runs: [number, number][] = []
  for (const year of sorted) {
    const last = runs[runs.length - 1]
    if (last && year === last[1] + 1) last[1] = year
    else runs.push([year, year])
  }
  return runs
}

export interface TimelineProps {
  series: TimelineSeries[]
  measure?: TimelineMeasure
  /** The first year read from digital editions, where the two eras meet. */
  digitalFrom?: number | null
  /** Years the archive holds no edition for. */
  missingYears?: number[]
  onPickYear?: (year: number, term: string | null) => void
  height?: number
}

export function Timeline({
  series,
  measure = 'rate',
  digitalFrom = null,
  missingYears = [],
  onPickYear,
  height = 190,
}: TimelineProps) {
  const [hover, setHover] = useState<number | null>(null)
  const years = useMemo(() => yearsOf(series), [series])

  if (years.length === 0 || series.length === 0) return null

  const first = years[0]
  const last = years[years.length - 1]
  const span = Math.max(1, years.length - 1)
  // A single year has no span to place itself along, so it sits in the middle
  // rather than pinned to the left edge where it reads as a truncated series.
  const x = (year: number) => (years.length === 1 ? 50 : ((year - first) / span) * 100)
  const peak = Math.max(
    ...series.flatMap((line) => line.points.map((point) => valueOf(point, measure))),
    1,
  )
  const y = (value: number) => height - 6 - (value / peak) * (height - 16)

  const lookup = series.map(
    (line) =>
      new Map(
        line.points.map(
          (point) => [Number(point.period.slice(0, 4)), point] as [number, TimelinePoint],
        ),
      ),
  )
  const runs = floorRuns(series, years)
  const halfBand = 100 / span / 2

  const path = (index: number) => {
    const map = lookup[index]
    const out: string[] = []
    let pen = false
    for (const year of years) {
      const point = map.get(year)
      if (!point) {
        // A year with no measurement breaks the line rather than being drawn
        // through: a continuous line across a gap asserts a value nobody has.
        pen = false
        continue
      }
      out.push(`${pen ? 'L' : 'M'}${x(year).toFixed(2)} ${y(valueOf(point, measure)).toFixed(1)}`)
      pen = true
    }
    return out.join(' ')
  }

  const missing = missingYears.filter((year) => year >= first && year <= last)
  // Where the two eras meet: the server's own floor flag when it sends one,
  // otherwise whatever the caller worked out from the editions held.
  const boundary = runs.length > 0 && runs[0][0] === first ? runs[0][1] + 1 : digitalFrom
  const scannedWidth = boundary && boundary > first ? x(boundary) : 0
  const ticks = axisTicks(years, boundary)
  const hovered = hover === null ? null : years[hover]

  const scannedBands: [number, number][] =
    runs.length > 0
      ? runs.map(([from, to]) => [
          Math.max(0, x(from) - halfBand),
          Math.min(100, x(to) + halfBand),
        ])
      : scannedWidth > 0
        ? [[0, scannedWidth]]
        : []
  // Contiguous runs of missing years, so a whole absent decade is one band
  // rather than thirty-odd stripes.
  const missingBands = runsOf(missing)
  /*
   * The two-era caption and the divider between them are printed only when the
   * data actually tells that story: one run of scanned years at the start,
   * wide enough to label, with a digital stretch after it. A corpus whose
   * scanned years are scattered has no boundary to draw, and drawing one there
   * would assert an era change that did not happen. The per-point ring below
   * marks a floor value in every other case.
   */
  const eraDivider =
    runs.length === 1 && runs[0][0] === first && scannedWidth >= 14 && scannedWidth <= 86
  // Four gridlines, so the plot has a scale to read against.
  const gridValues = [0, 1, 2, 3, 4].map((step) => (peak * (4 - step)) / 4)

  return (
    // The left gutter holds the y axis, so the era captions and the plot's own
    // left edge are not fighting for the same 30 pixels.
    <div className="relative pl-[34px]">
      <div className="pointer-events-none absolute left-0 top-0 h-[200px] w-[30px]">
        {gridValues.map((value, step) => (
          <span
            key={`g${step}`}
            className="absolute right-0 font-mono text-[9.5px] text-ink2"
            style={{ top: `${(step / 4) * 200}px`, transform: 'translateY(-50%)' }}
          >
            {measure === 'rate' ? value.toFixed(0) : Math.round(value)}
          </span>
        ))}
      </div>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="block h-[200px] w-full overflow-visible"
        role="img"
        aria-label={`${series.map((line) => line.term).join(', ')} over ${first} to ${last}`}
        onMouseLeave={() => setHover(null)}
      >
        {scannedBands.map(([left, right]) => (
          <rect
            key={`s${left}`}
            x={left}
            y={0}
            width={Math.max(0, right - left)}
            height={height}
            fill="var(--color-neutral-200)"
            opacity={0.55}
          />
        ))}

        {/* Grid over the bands, so a shaded era still has a scale to read against. */}
        {[0, 1, 2, 3, 4].map((step) => (
          <line
            key={step}
            x1={0}
            x2={100}
            y1={(height / 4) * step}
            y2={(height / 4) * step}
            stroke="var(--color-neutral-300)"
            strokeWidth={0.6}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Later series first, so the accent line reads on top. */}
        {series
          .map((line, index) => ({ line, index }))
          .reverse()
          .map(({ index }) => (
            <path
              key={index}
              d={path(index)}
              fill="none"
              stroke={SERIES_COLOURS[index]}
              strokeWidth={index === 0 ? 2 : 1.6}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}

        {eraDivider && (
          <line
            x1={scannedWidth}
            x2={scannedWidth}
            y1={0}
            y2={height}
            stroke="var(--color-text)"
            strokeWidth={1}
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {hovered !== null && (
          <line
            x1={x(hovered)}
            x2={x(hovered)}
            y1={0}
            y2={height}
            stroke="var(--jl-accent)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            opacity={0.6}
          />
        )}

        {/* Hit targets: one column per year, wider than any mark. */}
        {years.map((year, index) => (
          <rect
            key={year}
            x={Math.max(0, x(year) - halfBand)}
            y={0}
            width={halfBand * 2}
            height={height}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
            onFocus={() => setHover(index)}
            onClick={() => onPickYear?.(year, null)}
            style={{ cursor: onPickYear ? 'pointer' : 'default' }}
          />
        ))}
      </svg>

      {/*
        Bands, markers, labels and gridline values are HTML rather than SVG,
        because the plot is drawn with preserveAspectRatio="none": inside it a
        circle would be squashed into an ellipse and a 45° hatch flattened into
        horizontal stripes by the same transform that makes the line fill the
        width. Each marker carries a paper ring so overlapping series stay
        countable, and every series is named at its last point as well as in
        the legend, so identity is never colour alone.
      */}
      <div className="pointer-events-none absolute left-[34px] right-0 top-0 h-[200px]">
        {/* Years the archive holds no edition for, hatched as the legend draws them. */}
        {missingBands.map(([from, to]) => (
          <span
            key={`m${from}`}
            className="absolute inset-y-0 block"
            style={{
              left: `${Math.max(0, x(from) - halfBand)}%`,
              width: `${Math.min(100, x(to) + halfBand) - Math.max(0, x(from) - halfBand)}%`,
              background:
                'repeating-linear-gradient(135deg,var(--color-accent-2-100) 0 4px,transparent 4px 8px)',
              // No edge rule: a magenta hairline beside a data point reads as a
              // mark on the chart rather than as the boundary of an absence.
              opacity: 0.75,
            }}
          />
        ))}

        {eraDivider && (
          <>
            <span
              className="absolute font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2"
              style={{ left: 8, top: 6 }}
            >
              Scanned · a floor
            </span>
            <span
              className="absolute font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2"
              style={{ left: `calc(${scannedWidth}% + 8px)`, top: 6 }}
            >
              Digital · near-exact
            </span>
          </>
        )}

        {series.map((line, index) => {
          const map = lookup[index]
          const drawn = years.filter((year) => map.has(year))
          const label = drawn[drawn.length - 1]
          return (
            <div key={line.term}>
              {drawn.length <= 40 &&
                drawn.map((year) => {
                  const point = map.get(year) as TimelinePoint
                  return (
                    <span
                      key={year}
                      className="absolute block h-[9px] w-[9px] rounded-full"
                      style={{
                        left: `${x(year)}%`,
                        top: `${(y(valueOf(point, measure)) / height) * 200}px`,
                        marginLeft: -4.5,
                        marginTop: -4.5,
                        background: SERIES_COLOURS[index],
                        // A value read off a scan is a floor. The proof-reader's
                        // ring says so on the mark itself, at any data density,
                        // where a shaded era only says it when the era is wide.
                        boxShadow: point.floor
                          ? '0 0 0 2px var(--color-bg), 0 0 0 3.5px var(--color-accent-2-700)'
                          : '0 0 0 2px var(--color-bg)',
                      }}
                    />
                  )
                })}
              {label !== undefined && (
                <span
                  className="absolute whitespace-nowrap text-[12px] font-semibold"
                  style={{
                    left: `${x(label)}%`,
                    top: `${(y(valueOf(map.get(label) as TimelinePoint, measure)) / height) * 200}px`,
                    // The label sits on whichever side of its point has room,
                    // so a series ending near either edge is not cut off.
                    transform:
                      x(label) > 55
                        ? 'translate(-100%, -160%)'
                        : 'translate(6px, -160%)',
                    color: SERIES_COLOURS[index],
                  }}
                >
                  {line.term}
                </span>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-[7px] flex justify-between font-mono text-[10px] text-ink2">
        {ticks.map((tick) => (
          <div key={tick.year} className={tick.emphasis ? 'text-ink' : undefined}>
            {tick.year}
          </div>
        ))}
      </div>

      {hovered !== null && (
        <TimelineTooltip
          year={hovered}
          left={x(hovered)}
          series={series}
          lookup={lookup}
          measure={measure}
          missing={missing.includes(hovered)}
        />
      )}
    </div>
  )
}

function TimelineTooltip({
  year,
  left,
  series,
  lookup,
  measure,
  missing,
}: {
  year: number
  left: number
  series: TimelineSeries[]
  lookup: Map<number, TimelinePoint>[]
  measure: TimelineMeasure
  missing: boolean
}) {
  return (
    <div
      className="popover pointer-events-none absolute top-2 z-10 w-[228px] p-2.5"
      style={{
        left: `${left}%`,
        transform: left > 60 ? 'translateX(-100%) translateX(-10px)' : 'translateX(10px)',
      }}
      role="status"
    >
      <div className="mb-1.5 font-mono text-[11px] text-ink">{year}</div>
      {missing && (
        <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-mark">
          No edition held
        </div>
      )}
      {series.map((line, index) => {
        const point = lookup[index].get(year)
        return (
          <div key={line.term} className="flex items-baseline gap-2 py-[1px]">
            <span
              className="mt-[3px] h-[9px] w-[9px] flex-none"
              style={{ background: SERIES_COLOURS[index] }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{line.term}</span>
            <span className="flex-none font-mono text-[11px] text-ink">
              {point
                ? measure === 'rate'
                  ? rateOf(point).toFixed(1)
                  : String(point.matches)
                : '–'}
            </span>
          </div>
        )
      })}
      <div className="mt-1.5 border-t border-n200 pt-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink2">
        {measure === 'rate' ? 'per 10,000 units' : 'matching units'}
        {series[0] && lookup[0].get(year)
          ? ` · of ${lookup[0].get(year)?.total ?? 0} searched`
          : ''}
      </div>
    </div>
  )
}

function axisTicks(
  years: number[],
  digitalFrom: number | null,
): { year: number; emphasis: boolean }[] {
  if (years.length <= 7) {
    return years.map((year) => ({ year, emphasis: year === digitalFrom }))
  }
  const last = years.length - 1
  const indexes = [0, 1, 2, 3, 4, 5, 6].map((step) => Math.round((step / 6) * last))
  const unique = [...new Set(indexes)]
  return unique.map((index) => ({
    year: years[index],
    emphasis: years[index] === digitalFrom,
  }))
}

/** The single-series form the search screen used: kept so a caller can hand it one query. */
export function timelineSeries(data: TimelineData): TimelineSeries[] {
  if (data.series?.length) {
    return data.series.map((line) => ({ term: line.term, points: line.points }))
  }
  return [{ term: data.query, points: data.points }]
}
